import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, type Session, type AuditEntry, type PanelUser, timeAgo } from '../lib/api'
import { useAuth } from '../lib/auth'
import { PageHeader, Card, Button, Field, Input, Select, ErrorBox, Modal, Badge, Table } from '../components/ui'
import { IconTrash, IconPlus } from '../components/icons'

const ACTION_RU: Record<string, string> = {
  login_ok: 'вход',
  login_fail: 'неудачный вход',
  logout: 'выход',
  password_change: 'смена пароля',
  user_create: 'создан пользователь',
  user_delete: 'удалён пользователь',
}
const ACTION_TONE: Record<string, 'green'|'red'|'blue'|'yellow'|'neutral'> = {
  login_ok: 'green', login_fail: 'red', logout: 'neutral',
  password_change: 'blue', user_create: 'blue', user_delete: 'yellow',
}

export function AccountPage() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const [tab, setTab] = useState<'profile'|'sessions'|'audit'|'users'>('profile')

  const isAdmin = user?.role === 'admin'

  return (
    <>
      <PageHeader title="Настройки профиля" subtitle="Управление аккаунтом, сессиями и другими пользователями панели." />
      <div className="flex gap-1 mb-5 border-b border-neutral-200">
        {([
          ['profile', 'Пароль'],
          ['sessions', 'Сессии'],
          ['audit', 'Журнал'],
          ...(isAdmin ? [['users', 'Пользователи панели']] : []),
        ] as [string, string][]).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k as any)}
            className={`px-4 py-2 text-[13px] border-b-2 -mb-px transition-colors ${
              tab === k
                ? 'border-neutral-900 text-neutral-900 font-medium'
                : 'border-transparent text-neutral-500 hover:text-neutral-900'
            }`}>{l}</button>
        ))}
      </div>

      {tab === 'profile' && <ProfileTab />}
      {tab === 'sessions' && <SessionsTab qc={qc} />}
      {tab === 'audit' && <AuditTab />}
      {tab === 'users' && isAdmin && <UsersTab />}
    </>
  )
}

function ProfileTab() {
  const [f, setF] = useState({ old: '', new1: '', new2: '' })
  const [err, setErr] = useState('')
  const [ok, setOk] = useState(false)
  const submit = useMutation({
    mutationFn: async () => api.post('/auth/password', { old_password: f.old, new_password: f.new1 }),
    onSuccess: () => { setOk(true); setF({ old: '', new1: '', new2: '' }) },
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Card className="max-w-md p-6">
      <h2 className="text-[15px] font-semibold mb-4">Смена пароля</h2>
      <form onSubmit={(e) => {
        e.preventDefault(); setErr(''); setOk(false)
        if (f.new1 !== f.new2) { setErr('Пароли не совпадают'); return }
        submit.mutate()
      }} className="space-y-4">
        <Field label="Текущий пароль"><Input type="password" value={f.old} onChange={(e) => setF({ ...f, old: e.target.value })} required /></Field>
        <Field label="Новый пароль" hint="Минимум 8 символов"><Input type="password" value={f.new1} onChange={(e) => setF({ ...f, new1: e.target.value })} required minLength={8} /></Field>
        <Field label="Повторите новый пароль"><Input type="password" value={f.new2} onChange={(e) => setF({ ...f, new2: e.target.value })} required /></Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        {ok && <div className="text-[13px] text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">Пароль изменён. Другие сессии отозваны.</div>}
        <Button type="submit" disabled={submit.isPending}>{submit.isPending ? 'Сохраняем…' : 'Сменить пароль'}</Button>
      </form>
    </Card>
  )
}

function SessionsTab({ qc }: { qc: any }) {
  const { data: sessions = [] } = useQuery({
    queryKey: ['sessions'],
    queryFn: async () => (await api.get<Session[]>('/auth/sessions')).data,
    refetchInterval: 30000,
  })
  const revoke = useMutation({
    mutationFn: async (id: number) => api.delete(`/auth/sessions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  })
  return (
    <Table<Session>
      rows={sessions}
      empty="Нет активных сессий."
      cols={[
        { key: 'ip', label: 'IP', render: (s) => <span className="font-mono text-[12px]">{s.ip_address || '—'}</span> },
        { key: 'ua', label: 'Клиент', render: (s) => <span className="text-[12px] text-neutral-600 truncate max-w-[400px] block">{s.user_agent || '—'}</span> },
        { key: 'c', label: 'Создана', render: (s) => <span className="text-[12px] text-neutral-500">{new Date(s.created_at).toLocaleString('ru-RU')}</span> },
        { key: 'l', label: 'Активность', render: (s) => <span className="text-[12px] text-neutral-500">{s.last_used_at ? timeAgo(s.last_used_at) : '—'}</span> },
        { key: 'st', label: 'Статус', render: (s) =>
          s.revoked_at
            ? <Badge tone="red">отозвана</Badge>
            : s.current ? <Badge tone="green">текущая</Badge> : <Badge tone="blue">активна</Badge> },
        { key: 'act', label: '', w: '1%', render: (s) =>
          !s.revoked_at && !s.current && (
            <Button variant="ghost" size="sm" onClick={() => confirm('Завершить сессию?') && revoke.mutate(s.id)}>Завершить</Button>
          ) },
      ]}
    />
  )
}

function AuditTab() {
  const { data: log = [] } = useQuery({
    queryKey: ['auth-audit'],
    queryFn: async () => (await api.get<AuditEntry[]>('/auth/audit')).data,
    refetchInterval: 15000,
  })
  return (
    <Table<AuditEntry>
      rows={log}
      empty="Журнал пуст."
      cols={[
        { key: 't', label: 'Время', render: (e) => <span className="text-[12px] text-neutral-600 tabular-nums">{new Date(e.ts).toLocaleString('ru-RU')}</span> },
        { key: 'a', label: 'Действие', render: (e) => <Badge tone={ACTION_TONE[e.action] || 'neutral'}>{ACTION_RU[e.action] || e.action}</Badge> },
        { key: 'em', label: 'Email', render: (e) => <span className="text-[13px]">{e.email}</span> },
        { key: 'ip', label: 'IP', render: (e) => <span className="font-mono text-[12px] text-neutral-600">{e.ip_address || '—'}</span> },
        { key: 'd', label: 'Детали', render: (e) => <span className="text-[12px] text-neutral-500 truncate max-w-[300px] block">{e.details || '—'}</span> },
      ]}
    />
  )
}

function UsersTab() {
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const { data: users = [] } = useQuery({
    queryKey: ['panel-users'],
    queryFn: async () => (await api.get<PanelUser[]>('/panel-users')).data,
  })
  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/panel-users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['panel-users'] }),
  })
  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button onClick={() => setAddOpen(true)}><IconPlus /> Добавить пользователя</Button>
      </div>
      <Table<PanelUser>
        rows={users}
        empty="Нет пользователей."
        cols={[
          { key: 'e', label: 'Email', render: (u) => <span className="font-medium">{u.email}</span> },
          { key: 'n', label: 'Имя', render: (u) => <span className="text-[13px] text-neutral-600">{u.display_name || '—'}</span> },
          { key: 'r', label: 'Роль', render: (u) => <Badge tone={u.role === 'admin' ? 'purple' : 'neutral'}>{u.role}</Badge> },
          { key: 's', label: 'Статус', render: (u) => u.is_active ? <Badge tone="green">активен</Badge> : <Badge>отключён</Badge> },
          { key: 'act', label: '', w: '1%', render: (u) => (
            <Button variant="ghost" size="sm" title="Удалить"
              onClick={() => confirm(`Удалить ${u.email}?`) && del.mutate(u.id)}>
              <IconTrash />
            </Button>
          )},
        ]}
      />
      {addOpen && <AddUserModal onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); qc.invalidateQueries({ queryKey: ['panel-users'] }) }} />}
    </div>
  )
}

function AddUserModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [f, setF] = useState({ email: '', password: '', role: 'admin', display_name: '' })
  const [err, setErr] = useState('')
  const create = useMutation({
    mutationFn: async () => api.post('/panel-users', f),
    onSuccess: onCreated,
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title="Добавить пользователя панели">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate() }} className="space-y-4">
        <Field label="Email"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required autoFocus /></Field>
        <Field label="Пароль" hint="Минимум 8 символов"><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required minLength={8} /></Field>
        <Field label="Имя (необязательно)"><Input value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} /></Field>
        <Field label="Роль">
          <Select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
            <option value="admin">Администратор</option>
          </Select>
        </Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100 -mx-5 px-5">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={create.isPending}>Создать</Button>
        </div>
      </form>
    </Modal>
  )
}
